import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
  type ChartData,
  type Plugin,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import styles from './cpForecastJobsChart.module.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ---- helpers ----------------------------------------------------------------

function fmtMonth(iso: string): string {
  const [year, month] = iso.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

// ---- average-line plugin (scoped, not registered globally) ------------------

function buildAvgLinePlugin(avg: number): Plugin<'bar'> {
  return {
    id: 'cpAvgLine',
    afterDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea || !scales['y']) return;
      const yScale = scales['y'];
      const yPos   = yScale.getPixelForValue(avg);
      if (yPos < chartArea.top || yPos > chartArea.bottom) return;

      ctx.save();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.moveTo(chartArea.left,  yPos);
      ctx.lineTo(chartArea.right, yPos);
      ctx.stroke();
      ctx.restore();
    },
  };
}

// ---- component --------------------------------------------------------------

const CPForecastJobsChart = observer(() => {
  const store = useCapacityStore();
  const { dashboardData } = store;

  if (!dashboardData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
            <i className="pi pi-briefcase" style={{ marginRight: 8 }} />
            Forecast Jobs / Month
          </span>
        </div>
        <div className={styles.chartWrap} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>No data available</span>
        </div>
      </div>
    );
  }

  const months = store.filteredMonths;
  const { forecast_jobs } = dashboardData;
  const labels = months.map(fmtMonth);
  const values = months.map((m) => forecast_jobs[m] ?? 0);
  const avg    = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;

  const chartData: ChartData<'bar'> = {
    labels,
    datasets: [
      {
        label: 'Forecast Jobs',
        data: values,
        backgroundColor: 'rgba(82,45,110,0.7)',
        hoverBackgroundColor: '#522d6e',
        borderRadius: 4,
        barThickness: 28,
      },
    ],
  };

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const y = ctx.parsed.y ?? 0;
            return ` ${y.toFixed(1)} jobs`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f1f5f9' },
        ticks: { font: { size: 11 } },
      },
    },
  };

  const avgPlugin = buildAvgLinePlugin(avg);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
          <i className="pi pi-briefcase" style={{ marginRight: 8 }} />
          Forecast Jobs / Month
        </span>
        <span className={styles.avgBadge}>Avg {avg.toFixed(1)}</span>
      </div>
      <div className={styles.chartWrap}>
        <Bar data={chartData} options={chartOptions} plugins={[avgPlugin]} />
      </div>
    </div>
  );
});

export default CPForecastJobsChart;
